import { Check, Pencil, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { request } from "@/lib/api";
import { useDebouncedCallback } from "@/hooks/useDebouncedCallback";
import { useAuth } from "@/state/AuthContext";
import { useOrganizationStore } from "@/stores/organizationStore";
import { isPlatformAdmin } from "@/utils/access";

export type OrgProfile = {
  name: string;
  industry: string;
  website: string;
  description: string;
  timezone: string;
};

type OrgProfileResponse = Partial<OrgProfile> & { domain?: string | null };

type OrgProfileOverviewResponse = {
  profile?: OrgProfileResponse;
  data?: { profile?: OrgProfileResponse };
};

const INDUSTRY_OPTIONS = [
  "Technology",
  "Finance",
  "Healthcare",
  "Construction",
  "Consulting",
  "Government",
  "Education",
  "Other",
];

const NAME_LIMIT = 255;

export function OrganizationProfileForm() {
  const { user } = useAuth();
  const canRename = isPlatformAdmin(user);
  const currentOrganization = useOrganizationStore((s) => s.currentOrganization);
  const organizations = useOrganizationStore((s) => s.organizations);
  const setCurrentOrganization = useOrganizationStore((s) => s.setCurrentOrganization);
  const setOrganizations = useOrganizationStore((s) => s.setOrganizations);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<OrgProfile>({
    name: "",
    industry: "Technology",
    website: "",
    description: "",
    timezone: "UTC",
  });
  const [serverZones, setServerZones] = useState<string[]>([]);
  const [zoneOpen, setZoneOpen] = useState(false);
  const [zoneQuery, setZoneQuery] = useState("");
  const latestOkRef = useRef<OrgProfile | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const overview = await request.get<OrgProfileOverviewResponse>(
          "/admin/organization/overview",
        );
        const zonesPayload = await request.get<{
          timezones?: string[];
          data?: { timezones?: string[] };
        }>("/timezones");
        if (!active) return;
        const zones = zonesPayload?.timezones || zonesPayload?.data?.timezones || [];
        setServerZones(zones.filter((zone) => typeof zone === "string"));
        const p: OrgProfileResponse = overview?.profile || overview?.data?.profile || {};
        const next: OrgProfile = {
          name: p.name || "",
          industry: p.industry || "Technology",
          website: p.website || p.domain || "",
          description: p.description || "",
          timezone: p.timezone || "UTC",
        };
        setProfile(next);
        latestOkRef.current = next;
      } catch {
        if (active) setError("Failed to load organization profile.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const persist = useCallback(async (next: OrgProfile) => {
    setSaving(true);
    setError(null);
    try {
      await request.patch("/admin/organization/profile", next);
      latestOkRef.current = next;
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
      const message =
        typeof data?.message === "string" && data.message.trim()
          ? data.message
          : "Could not save organization profile.";
      setError(message);
      toast.error(message);
      if (latestOkRef.current) {
        setProfile(latestOkRef.current);
      }
    } finally {
      setSaving(false);
    }
  }, []);

  const debouncedPersist = useDebouncedCallback((next: OrgProfile) => {
    void persist(next);
  }, 600);

  const beginRename = () => {
    setNameDraft(profile.name);
    setNameError(null);
    setEditingName(true);
  };

  const cancelRename = () => {
    setNameDraft(profile.name);
    setNameError(null);
    setEditingName(false);
  };

  const commitRename = async () => {
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      setNameError("Enter an organization name.");
      return;
    }
    setNameError(null);
    const next = { ...profile, name: trimmed };
    setSaving(true);
    setError(null);
    try {
      await request.patch("/admin/organization/profile", next);
      setProfile(next);
      latestOkRef.current = next;
      setEditingName(false);
      if (currentOrganization) {
        setCurrentOrganization({ ...currentOrganization, name: trimmed });
        setOrganizations(
          organizations.map((org) =>
            org.id === currentOrganization.id ? { ...org, name: trimmed } : org,
          ),
        );
      }
    } catch (err: unknown) {
      const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
      const message =
        typeof data?.message === "string" && data.message.trim()
          ? data.message
          : "Could not save organization profile.";
      setNameError(message);
      setNameDraft(profile.name);
      setEditingName(false);
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof OrgProfile, value: string) => {
    if (field === "name") return;
    setProfile((prev) => {
      const next = { ...prev, [field]: value };
      debouncedPersist(next);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <p className="text-sm text-gray-500">Loading organization profile...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      <div className="space-y-5 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm text-gray-600">Changes save automatically.</p>
          {saving ? (
            <span className="text-xs font-medium text-neutral-500" aria-live="polite">
              Saving…
            </span>
          ) : null}
        </div>
        <div>
          <span className="block text-sm font-medium text-gray-700">Organization name</span>
          {editingName && canRename ? (
            <div className="mt-1">
              <div className="flex items-center gap-2">
                <input
                  id="org-name"
                  aria-label="Organization name"
                  type="text"
                  value={nameDraft}
                  maxLength={NAME_LIMIT}
                  autoFocus
                  onChange={(e) => {
                    setNameDraft(e.target.value);
                    if (nameError) setNameError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void commitRename();
                    }
                    if (e.key === "Escape") cancelRename();
                  }}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                />
                <button
                  type="button"
                  aria-label="Save organization name" title="Save organization name"
                  onClick={() => void commitRename()}
                  className="rounded p-1.5 text-gray-700 hover:bg-gray-100"
                >
                  <Check className="h-4 w-4" aria-hidden />
                </button>
                <button
                  type="button"
                  aria-label="Cancel organization rename" title="Cancel organization rename"
                  onClick={cancelRename}
                  className="rounded p-1.5 text-gray-700 hover:bg-gray-100"
                >
                  <X className="h-4 w-4" aria-hidden />
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">{nameDraft.length}/{NAME_LIMIT}</p>
              {nameError ? <p className="mt-1 text-sm text-red-700">{nameError}</p> : null}
            </div>
          ) : (
            <div className="mt-1 flex items-center gap-2">
              <p className="text-sm text-gray-900">{profile.name}</p>
              {canRename ? (
                <button
                  type="button"
                  aria-label="Rename organization" title="Rename organization"
                  onClick={beginRename}
                  className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                >
                  <Pencil className="h-4 w-4" aria-hidden />
                </button>
              ) : null}
            </div>
          )}
          {nameError && !editingName ? <p className="mt-1 text-sm text-red-700">{nameError}</p> : null}
        </div>

        <div>
          <label htmlFor="org-industry" className="block text-sm font-medium text-gray-700">
            Industry
          </label>
          <select
            id="org-industry"
            value={profile.industry}
            onChange={(e) => updateField("industry", e.target.value)}
            className="mt-1 w-full max-w-xs rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          >
            {INDUSTRY_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="org-website" className="block text-sm font-medium text-gray-700">
            Website URL <span className="text-gray-400">(optional)</span>
          </label>
          <input
            id="org-website"
            type="url"
            value={profile.website}
            onChange={(e) => updateField("website", e.target.value)}
            placeholder="https://example.com"
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label htmlFor="org-description" className="block text-sm font-medium text-gray-700">
            Description <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            id="org-description"
            rows={3}
            value={profile.description}
            onChange={(e) => updateField("description", e.target.value)}
            placeholder="Brief description of your organization"
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
        </div>

        <div>
          <label htmlFor="org-timezone" className="block text-sm font-medium text-gray-700">
            Time zone
          </label>
          <input
            id="org-timezone"
            role="combobox"
            aria-expanded={zoneOpen}
            aria-controls="org-timezone-listbox"
            aria-autocomplete="list"
            value={zoneOpen ? zoneQuery : profile.timezone}
            onFocus={() => {
              setZoneOpen(true);
              setZoneQuery("");
            }}
            onChange={(e) => {
              setZoneQuery(e.target.value);
              setZoneOpen(true);
            }}
            onBlur={() => setZoneOpen(false)}
            className="mt-1 w-full max-w-md rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
          {zoneOpen ? (
            <ul
              id="org-timezone-listbox"
              role="listbox"
              className="mt-1 max-h-48 w-full max-w-md overflow-auto rounded border border-gray-200 bg-white shadow"
            >
              {serverZones
                .filter((zone) => zone.toLowerCase().includes(zoneQuery.trim().toLowerCase()))
                .slice(0, 80)
                .map((zone) => (
                  <li key={zone}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={zone === profile.timezone}
                      className="w-full px-3 py-1.5 text-left text-sm hover:bg-gray-50"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        updateField("timezone", zone);
                        setZoneOpen(false);
                        setZoneQuery("");
                      }}
                    >
                      {zone}
                    </button>
                  </li>
                ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
}
